import { ReviewClip, ReviewClipKind } from 'modules/match-video/domain/interfaces';
import { sortReviewClips } from './build-review-timeline';

/** One file added twice is one clip: the same name and size (a Disk download gets a new `lastModified` each time). */
const sameFileKey = (clip: ReviewClip) =>
  clip.source.type === 'file' ? `${clip.name}:${clip.source.file.size}` : `${clip.name}:${clip.source.url}`;

export interface AddedReviewClips {
  clips: ReviewClip[];
  /** Files already in the list (or twice in this batch) that were not added again. */
  skipped: number;
}

/**
 * Adds clips to the list the user has. The list order is the review order and may have been changed by hand, so the
 * clips already there never move: a clip with a camera time goes before the first clip with a later time (after the
 * last timed one when none is later), a clip without one goes to the end.
 */
export function addReviewClips(current: readonly ReviewClip[], incoming: readonly ReviewClip[]): AddedReviewClips {
  const seen = new Set(current.map(sameFileKey));
  const fresh = incoming.filter((clip) => {
    const key = sameFileKey(clip);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const clips = [...current];
  sortReviewClips(fresh).forEach((clip) => {
    if (!clip.capturedAt) {
      clips.push(clip);
      return;
    }
    const at = clip.capturedAt;
    const later = clips.findIndex((item) => item.capturedAt !== undefined && item.capturedAt > at);
    if (later >= 0) {
      clips.splice(later, 0, clip);
      return;
    }
    const lastTimed = clips.reduce((last, item, index) => (item.capturedAt ? index : last), -1);
    clips.splice(lastTimed + 1, 0, clip);
  });

  return { clips, skipped: incoming.length - fresh.length };
}

/**
 * The clip moved to place `toIndex`, the others shift to make room — one move for the arrows (a neighbour's place) and
 * for dragging (any place). Out of range or unknown id — the list stays as it is.
 */
export function moveReviewClip(clips: readonly ReviewClip[], clipId: string, toIndex: number): ReviewClip[] {
  const from = clips.findIndex((clip) => clip.id === clipId);
  if (from < 0 || toIndex < 0 || toIndex >= clips.length || toIndex === from) return [...clips];
  const next = [...clips];
  const [moved] = next.splice(from, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

export const removeReviewClip = (clips: readonly ReviewClip[], clipId: string): ReviewClip[] =>
  clips.filter((clip) => clip.id !== clipId);

export const setReviewClipKind = (clips: readonly ReviewClip[], clipId: string, kind: ReviewClipKind): ReviewClip[] =>
  clips.map((clip) => (clip.id === clipId ? { ...clip, kind } : clip));
